import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Typography,
  MenuItem
} from '@mui/material';

/**
 * Rule Form Dialog
 * Add/Edit dialog for required document rules
 * Uses mock data during UI-First phase
 */
const RuleFormDialog = ({ open, onClose, onSave, rule, documentTypes, organizationCategories }) => {
  const [formData, setFormData] = useState({
    documentTypeId: '',
    organizationCategory: 'ALL',
    conditions: '',
    isMandatory: true,
    missingMessage: '',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEdit = !!rule;

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (rule) {
        setFormData({
          documentTypeId: rule.documentTypeId || '',
          organizationCategory: rule.organizationCategory || 'ALL',
          conditions: rule.conditions || '',
          isMandatory: rule.isMandatory !== undefined ? rule.isMandatory : true,
          missingMessage: rule.missingMessage || '',
          isActive: rule.isActive !== undefined ? rule.isActive : true
        });
      } else {
        setFormData({
          documentTypeId: documentTypes[0]?.id || '',
          organizationCategory: 'ALL',
          conditions: '',
          isMandatory: true,
          missingMessage: '',
          isActive: true
        });
      }
      setErrors({});
    }
  }, [open, rule, documentTypes]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleDocTypeChange = (event) => {
    const docTypeId = event.target.value;
    const docType = documentTypes.find(t => t.id === docTypeId);
    setFormData(prev => ({
      ...prev,
      documentTypeId: docTypeId,
      missingMessage: prev.missingMessage || `${docType?.name || 'Document'} is required`
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.documentTypeId) {
      newErrors.documentTypeId = 'Document type is required';
    }

    if (!formData.missingMessage.trim()) {
      newErrors.missingMessage = 'Missing message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isEdit ? 'Edit Required Document Rule' : 'Add Required Document Rule'}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Document Type */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Document Type"
              value={formData.documentTypeId}
              onChange={handleDocTypeChange}
              error={!!errors.documentTypeId}
              helperText={errors.documentTypeId}
              fullWidth
              required
            >
              {documentTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Organization Category */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Organization Category"
              value={formData.organizationCategory}
              onChange={handleChange('organizationCategory')}
              fullWidth
              helperText="Select 'All Categories' to apply to all"
            >
              <MenuItem value="ALL">All Categories</MenuItem>
              {organizationCategories.map((category) => (
                <MenuItem key={category.code} value={category.code}>
                  {category.label || category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Mandatory */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isMandatory}
                  onChange={handleChange('isMandatory')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Mandatory</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.isMandatory ? 'Document is required' : 'Document is recommended'}
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Active */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={handleChange('isActive')}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Inactive rules won't be evaluated
                  </Typography>
                </Box>
              }
            />
          </Grid>

          {/* Conditions */}
          <Grid item xs={12}>
            <TextField
              label="Conditions"
              value={formData.conditions}
              onChange={handleChange('conditions')}
              fullWidth
              multiline
              rows={2}
              placeholder="e.g., hasVAT=true, employeeCount>50"
              helperText="Optional conditions for when this rule applies"
            />
          </Grid>

          {/* Missing Message */}
          <Grid item xs={12}>
            <TextField
              label="Missing Message"
              value={formData.missingMessage}
              onChange={handleChange('missingMessage')}
              error={!!errors.missingMessage}
              helperText={errors.missingMessage || 'Message shown when document is missing'}
              fullWidth
              required
              multiline
              rows={2}
              placeholder="e.g., Trade License is required for all companies"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleFormDialog;
